/**
 * ChainPositionStrip — a one-line "you are here" orientation band
 * for the individual Health pages.
 *
 * Every Health page maps to one stage of the Learning Impact Chain
 * (Learning → Capability → Behavior → Business Outcomes →
 * Financial Impact). This strip renders the full chain inline,
 * highlights the current stage, lets the user jump to any stage,
 * and — via the shared `useChainLeak` detector — auto-highlights the
 * stage with the steepest quarter-over-quarter drop (the live
 * "value leak") with a pulsing indicator + a "jump to the leak"
 * CTA, so the next-best click is visually obvious during a demo.
 *
 * Chain config + leak detection are centralised in `lib/chainLeak`
 * so this strip, the home dashboard chain, and the guided tour all
 * agree on a single source of truth.
 *
 * Engine codes are kept as a small secondary hint per the
 * Missing Middle terminology standard.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, GitBranch, ArrowRight, ArrowLeft, Droplet, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CHAIN_STAGES } from '../../lib/chainLeak';
import { useChainLeak } from '../../hooks/useChainLeak';
import { useLeiReinforcement } from '../../hooks/useLeiReinforcement';

export function ChainPositionStrip({ stage, testId = 'chain-position-strip' }) {
  const navigate = useNavigate();
  const [leak] = useChainLeak();
  const { mode: leiMode, byStage: reinforceByStage } = useLeiReinforcement();

  const idx = CHAIN_STAGES.findIndex((s) => s.key === stage);
  if (idx === -1) return null;

  const next = idx < CHAIN_STAGES.length - 1 ? CHAIN_STAGES[idx + 1] : null;
  const prev = idx > 0 ? CHAIN_STAGES[idx - 1] : null;
  const leakNode = leak?.stage || null;
  // The "jump to the leak" CTA only makes sense when the leak is on
  // a *different* stage than the one we're already viewing.
  const leakElsewhere = leakNode && leakNode.key !== stage;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 flex items-center gap-x-3 gap-y-2 flex-wrap"
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">
        <GitBranch className="h-3 w-3" />
        Impact Chain
      </span>

      <div className="flex items-center gap-1 flex-wrap min-w-0">
        {CHAIN_STAGES.map((s, i) => {
          const isCurrent = s.key === stage;
          const isLeak = leak?.key === s.key;
          const reinforced = leiMode === 'conjoined' ? (reinforceByStage?.[s.key] || null) : null;
          const hasReinforcement = reinforced && reinforced.length > 0;
          return (
            <React.Fragment key={s.key}>
              {i > 0 && <ChevronRight className="h-3 w-3 text-slate-300 shrink-0" aria-hidden="true" />}
              <button
                type="button"
                onClick={() => { if (!isCurrent) navigate(s.path); }}
                disabled={isCurrent}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors shrink-0',
                  isCurrent
                    ? 'bg-slate-900 text-white cursor-default'
                    : 'text-slate-600 hover:bg-slate-200/70',
                  isLeak && 'ring-2 ring-amber-400 ring-offset-1 ring-offset-slate-50',
                )}
                data-testid={`${testId}-node-${s.key}`}
                title={
                  isLeak
                    ? `Steepest drop this quarter (${leak.deltaPct}%) — likely value leak`
                    : (isCurrent ? `You are here: ${s.label}` : `Jump to ${s.label} (${s.hint})`)
                }
              >
                {isLeak && (
                  <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                  </span>
                )}
                {s.label}
                <span className={cn('text-[8px] uppercase tracking-wider', isCurrent ? 'text-white/60' : 'text-slate-400')}>
                  {s.hint}
                </span>
                {hasReinforcement && (
                  <span
                    className={cn(
                      'ml-0.5 inline-flex items-center gap-0.5 rounded-full px-1 py-px text-[8px] font-bold uppercase tracking-wide',
                      isCurrent ? 'bg-teal-400/25 text-teal-100' : 'bg-teal-100 text-teal-700',
                    )}
                    data-testid={`${testId}-p5-badge-${s.key}`}
                    title={`Reinforced by P5 evidence (routed, non-weighted): ${reinforced.map((m) => m.name).join(', ')}`}
                  >
                    <Sparkles className="h-2 w-2" aria-hidden="true" />
                    P5
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {leakElsewhere ? (
        <button
          type="button"
          onClick={() => navigate(leakNode.path)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 hover:shadow-sm transition-all shrink-0"
          data-testid={`${testId}-jump-to-leak`}
          title={`This quarter's steepest drop is in ${leakNode.label} (${leak.deltaPct}%)`}
        >
          <Droplet className="h-3 w-3" />
          Jump to the leak
          <span className="text-amber-600">{leakNode.label}</span>
          <ArrowRight className="h-3 w-3" />
        </button>
      ) : next ? (
        <button
          type="button"
          onClick={() => navigate(next.path)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-400 hover:shadow-sm transition-all shrink-0"
          data-testid={`${testId}-follow`}
          title={`Follow the value chain downstream to ${next.label}`}
        >
          Follow the chain
          <span className="text-slate-500">{next.label}</span>
          <ArrowRight className="h-3 w-3" />
        </button>
      ) : prev ? (
        <button
          type="button"
          onClick={() => navigate(prev.path)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-400 hover:shadow-sm transition-all shrink-0"
          data-testid={`${testId}-trace-upstream`}
          title={`Trace the value leak upstream to ${prev.label}`}
        >
          <ArrowLeft className="h-3 w-3" />
          Trace upstream
          <span className="text-slate-500">{prev.label}</span>
        </button>
      ) : null}
    </div>
  );
}

export default ChainPositionStrip;
